import * as THREE from "three";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { clone as cloneSkeleton } from "three/examples/jsm/utils/SkeletonUtils.js";

const MODEL_BASE = "/hunt-assets/";

export const HUNT_ASSET_URLS = {
  hare: MODEL_BASE + "animal-rabbit.glb",
  fir: MODEL_BASE + "tree-fir-realistic.glb",
  firDistant: MODEL_BASE + "tree-fir-distant.glb",
  pine: MODEL_BASE + "tree-pine-1.glb",
  birch: MODEL_BASE + "tree-birch-1.glb",
  grass: MODEL_BASE + "grass-1.glb",
  tallGrass: MODEL_BASE + "grass-tall-1.glb",
  bush: MODEL_BASE + "bush-1.glb",
  fern: MODEL_BASE + "fern-1.glb",
  rock: MODEL_BASE + "rock-mossy-1.glb",
} as const;

type AssetKey = keyof typeof HUNT_ASSET_URLS;

type LoadedAsset = {
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
};

export type HareInstance = {
  root: THREE.Group;
  mixer: THREE.AnimationMixer | null;
  action: THREE.AnimationAction | null;
};

export class RealisticHuntAssets {
  private readonly loader = new GLTFLoader();
  private readonly dracoLoader = new DRACOLoader();
  private readonly cache = new Map<AssetKey, Promise<LoadedAsset>>();
  private readyPromise: Promise<void> | null = null;

  constructor() {
    this.dracoLoader.setDecoderPath("/draco/");
    this.dracoLoader.setWorkerLimit(2);
    this.dracoLoader.preload();

    this.loader.setDRACOLoader(this.dracoLoader);
    this.loader.setMeshoptDecoder(MeshoptDecoder);
  }

  prepare(includeHeavyFir = true): Promise<void> {
    if (!this.readyPromise) {
      const keys: AssetKey[] = [
        "hare",
        "firDistant",
        "pine",
        "birch",
        "grass",
        "tallGrass",
        "bush",
        "fern",
        "rock",
      ];

      if (includeHeavyFir) keys.push("fir");

      this.readyPromise = Promise.all(keys.map((key) => this.load(key))).then(
        () => undefined,
      );
    }

    return this.readyPromise;
  }

  async createHare(targetHeight = 0.62): Promise<HareInstance> {
    const source = await this.load("hare");
    const cloned = cloneSkeleton(source.scene) as THREE.Group;
    const root = this.normalizeToHeight(cloned, targetHeight);
    this.configureMeshes(root, true);

    const mixer = source.animations.length > 0 ? new THREE.AnimationMixer(root) : null;
    const locomotion =
      source.animations.find((clip) => /walk|run|hop|bound/i.test(clip.name)) ??
      source.animations[0] ??
      null;

    const action = mixer && locomotion ? mixer.clipAction(locomotion) : null;
    if (action) {
      action.reset();
      action.setLoop(THREE.LoopRepeat, Infinity);
      action.timeScale = 1.75;
      action.play();
    }

    return { root, mixer, action };
  }

  async createProp(
    key: Exclude<AssetKey, "hare">,
    targetHeight: number,
  ): Promise<THREE.Group> {
    const source = await this.load(key);
    const cloned = source.scene.clone(true);
    const root = this.normalizeToHeight(cloned, targetHeight);

    this.configureMeshes(root, false);
    return root;
  }

  private load(key: AssetKey): Promise<LoadedAsset> {
    const existing = this.cache.get(key);
    if (existing) return existing;

    const url = HUNT_ASSET_URLS[key];
    const request = this.loader
      .loadAsync(url)
      .then((gltf) => ({
        scene: gltf.scene,
        animations: gltf.animations,
      }))
      .catch((error: unknown) => {
        console.error("[hunt-assets] runtime GLB load failed", {
          key,
          url,
          error,
        });
        throw error;
      });

    this.cache.set(key, request);
    return request;
  }

  private normalizeToHeight(source: THREE.Group, targetHeight: number): THREE.Group {
    source.updateMatrixWorld(true);

    const initialBox = new THREE.Box3().setFromObject(source);
    const size = new THREE.Vector3();
    initialBox.getSize(size);

    const height = Math.max(size.y, 0.0001);
    source.scale.multiplyScalar(targetHeight / height);
    source.updateMatrixWorld(true);

    const scaledBox = new THREE.Box3().setFromObject(source);
    const center = new THREE.Vector3();
    scaledBox.getCenter(center);

    // Keep normalization on the child. Callers move the wrapper, so resetting a
    // world position never destroys the imported model's corrected pivot.
    source.position.x -= center.x;
    source.position.z -= center.z;
    source.position.y -= scaledBox.min.y;

    const wrapper = new THREE.Group();
    wrapper.add(source);
    return wrapper;
  }

  private configureMeshes(root: THREE.Object3D, animated: boolean): void {
    root.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;

      object.castShadow = true;
      object.receiveShadow = !animated;
      object.frustumCulled = true;

      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) {
        if ("envMapIntensity" in material) {
          (material as THREE.MeshStandardMaterial).envMapIntensity = 0.75;
        }
      }
    });
  }
}
